<template>
    <div id="projects_wrapper">
        <v-dialog
            v-model="projectDataDialog"
            transition="scale-transition"
            origin="center center"
            fullscreen
            style="z-index: 2000">
            <v-card class="d-flex align-start">
                <div style="width: 100%">
                    <ProjectDetails
                        :user_id="this_userId"
                        :project_id="this_projectId"
                        :client_info="this_clientInfo"
                        :project_statuses="project_statuses"
                        openTab="overview"
                        @closeDetailDialog="closeDetailDialog" />
                </div>
            </v-card>
        </v-dialog>

        <v-navigation-drawer
            v-model="projectActivityDialog"
            app
            relative
            right
            temporary
            width="46%"
            style="z-index: 2000">
            <v-card tile class="drawer-content pb-12">
                <v-card-title class="primary--text text--darken-2 font-weight-bold" :style="`border-bottom: 1px solid ${'primary-darken-2'}`">Activity Log</v-card-title>
                <v-card-text class="pt-3">
                    <ProjectActivity
                        :projectID="projectForActivity"
                        :userID="userForActivity" />
                </v-card-text>
            </v-card>
        </v-navigation-drawer>

        <div>
            <div class="pt-8 d-flex">
                <div class="sansation info--text d-inline px-4"><h2>Projects Dashboard</h2></div>
            </div>

            <div class="pt-0 pb-4 px-2 justify-space-between" flat>
                <div class="d-flex d-block justify-space-between">
                    <div class="d-inline px-3" style="min-width: 30%; max-width: 30%;">
                        <div class="d-block">
                            <div class="d-flex align-center">
                                <v-text-field 
                                    v-model="search"
                                    hint="Search for an existing project by user name, project title, etc."
                                    persistent-hint 
                                    single-line 
                                    color="primary darken-1" 
                                    append-icon="mdi-magnify"
                                    class="flex-grow-1">
                                </v-text-field>

                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            fab
                                            x-small
                                            elevation="1"
                                            color="background"
                                            class="ml-4 mb-1 pa-0 primary--text"
                                            :disabled="table_loading"
                                            @click="refreshProjects"
                                            v-bind="attrs"
                                            v-on="on">
                                            <v-icon :class="table_loading ? 'spin-refresh' : ''">mdi-autorenew</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Refresh projects from database</span>
                                </v-tooltip>
                            </div>
                        </div>

                        <div class="d-flex d-block mt-6">
                            <div class="d-inline mr-6">
                                <v-switch
                                    v-if="showFilterArchiveToggle"
                                    v-model="filterby.filter_archived"
                                    :label="`Archived Projects Only`"
                                    @change="getArchivedProjects()"
                                    hide-details>
                                </v-switch>
                            </div>

                            <div class="d-inline ml-6">
                                <v-switch
                                    v-model="filterby.is_annual"
                                    :label="`Annual Projects Only`"
                                    @change="filterProjects()"
                                    hide-details>
                                </v-switch>
                            </div>
                        </div>
                    </div>

                    <div class="d-inline" style="max-width: 70%;">
                        <div class="d-flex d-block justify-space-between">
                            <div class="d-inline" style="min-width: 40%; max-width: 70%;">
                                <v-autocomplete
                                    v-model="filterby.status"
                                    :items="project_statuses" 
                                    item-text="internal_label"
                                    item-value="value"
                                    label="Status" 
                                    :hint="filteredProjects.length ? project_status_metadata.join(', ') : ''"
                                    persistent-hint
                                    multiple
                                    clearable
                                    class="mr-3" 
                                    color="primary darken-1"
                                    @change="filterProjects()">
                                        
                                        <template v-slot:prepend-item>
                                            <v-list-item @click="toggleSelectAll">
                                                <v-list-item-action>
                                                    <!-- Use a checkbox to show selection state -->
                                                    <v-icon :color="allSelected ? 'primary' : ''">
                                                        {{ allSelected ? 'mdi-close-box' : 'mdi-checkbox-blank-outline' }}
                                                    </v-icon>
                                                </v-list-item-action>
                                                <v-list-item-content>
                                                    <v-list-item-title>
                                                        {{ allSelected ? 'Deselect All' : 'Select All' }}
                                                    </v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item>
                                            <v-divider></v-divider>
                                        </template>

                                        <template v-slot:message="{ message }">
                                            <span v-html="message"></span>
                                        </template>
                                </v-autocomplete>
                            </div>
                            
                            <div class="d-inline" style="min-width: 25%;">
                                <v-autocomplete
                                    v-model="filterby.current_stage"
                                    :items="current_stage_options"
                                    label="Current Stage"
                                    hide-details
                                    multiple
                                    clearable
                                    class="mx-3"
                                    color="primary darken-1"
                                    @change="filterProjects()">
                                </v-autocomplete>
                            </div>

                            <div class="d-inline" style="min-width: 25%;">
                                <v-autocomplete 
                                    v-model="filterby.module"
                                    :items="services" 
                                    label="Service(s)" 
                                    hide-details 
                                    multiple
                                    clearable
                                    class="mx-3" 
                                    color="primary darken-1"
                                    @change="filterProjects()">
                                </v-autocomplete>
                            </div>
                        </div>
                    
                        <div class="d-flex d-block justify-end mt-6 px-3">
                            <div class="d-inline-flex justify-space-between" style="gap: 20px;">
                                <!-- Initialized Date Range Picker -->
                                <v-menu
                                    ref="initDateMenu"
                                    v-model="initDateRangeMenu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            :value="initDateRangeText"
                                            label="Date Started Range"
                                            prepend-icon="mdi-calendar"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                            @click:clear="clearInitDateRange"
                                            hide-details
                                            style="width: 300px"
                                            color="primary darken-1"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="filterby.initialized_date_range"
                                        range
                                        no-title
                                        scrollable
                                        @input="filterby.initialized_date_range.length === 2 ? filterProjects() : null"
                                    >
                                        <v-spacer></v-spacer>
                                        <v-btn text color="primary" @click="initDateRangeMenu = false">Cancel</v-btn>
                                        <v-btn text color="primary" @click="filterProjects(); initDateRangeMenu = false">OK</v-btn>
                                    </v-date-picker>
                                </v-menu>

                                <!-- Close Date Range Picker -->
                                <v-menu
                                    ref="closeDateMenu"
                                    v-model="closeDateRangeMenu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            :value="closeDateRangeText"
                                            label="Completion Date Range"
                                            prepend-icon="mdi-calendar"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                            @click:clear="clearCloseDateRange"
                                            hide-details
                                            style="width: 300px"
                                            color="primary darken-1">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="filterby.close_date_range"
                                        range
                                        no-title
                                        scrollable
                                        @input="filterby.close_date_range.length === 2 ? filterProjects() : null"
                                    >
                                        <v-spacer></v-spacer>
                                        <v-btn text color="primary" @click="closeDateRangeMenu = false">Cancel</v-btn>
                                        <v-btn text color="primary" @click="filterProjects(); closeDateRangeMenu = false">OK</v-btn>
                                    </v-date-picker>
                                </v-menu>

                                <v-autocomplete 
                                    v-model="filterby.due_date"
                                    :items="due_date_filter" 
                                    label="Due Date" 
                                    hide-details 
                                    multiple 
                                    clearable
                                    class="d-inline"
                                    color="primary darken-1"
                                    @change="filterProjects()">
                                </v-autocomplete>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="d-flex justify-end align-center mt-3 px-4">
                    <span v-if="filtered_projects_cnt">{{ filtered_projects_cnt }} total results</span>
                    <v-btn
                        v-if="hasActiveFilters"
                        x-small
                        text
                        color="primary darken-1"
                        class="ml-3"
                        @click="clearFilters()">
                        Clear Filters
                    </v-btn>
                </div>
            </div>
        </div>

        <v-data-table
            :headers="headers"
            :items="searchedProjects"
            :loading="table_loading"
            loading-text="Pulling projects ..."
            :options.sync="options"
            :expanded.sync="expanded"
            item-key="dashboard_row_key"
            show-expand
            single-expand
            @update:options="onUpdateOptions"
            :footer-props="{ 'items-per-page-options': PAGE_SIZE_OPTIONS }"
            fixed-header
            :height="'calc(100vh - 380px)'" 
        >
        
            <template v-slot:no-data>
                <div class="text-center pt-12">
                    <v-icon large>mdi-alert-circle</v-icon>
                    <p class="mt-2">No projects found matching your filters.</p>
                </div>
            </template>

            <template v-slot:no-results>
                <div class="text-center pt-12">
                    <v-icon large>mdi-alert-circle</v-icon>
                    <p class="mt-2">No projects found matching your filters.</p>
                </div>
            </template>

            <!-- Custom Row Template -->
            <template v-slot:item="{ item, index, expand, isExpanded }">

                <tr :key="item.dashboard_row_key || `${item.project_id}-${item.user_id}-${index}`"
                    class="table-row">
                    <!-- Expand Icon -->
                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <UserData_Dashboard 
                            :key="item.project_id"
                            :user_data="item.client_info"
                            source="server" 
                        />
                        <div @click.stop="expand(!isExpanded)" 
                            class="mt-4 mb-1 primary--text text-decoration-none"
                            :class="isExpanded ? `font-weight-thin` : `font-weight-bold `">
                            <v-badge
                                v-if="item.quick_notes != null"
                                dot
                                left
                                inline
                                color="error">
                            </v-badge>
                            <span class="d-inline">View Notes</span>
                            <v-icon color="primary">
                                {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </v-icon>
                        </div>
                    </td>
                    <!-- Other Columns -->
                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <div class="d-flex align-top">
                            <div class="d-inline" v-html="item.module_name"></div>
                            <v-icon
                                v-if="item.is_annual"
                                small
                                color="error"
                                class="d-inline ml-1">
                                mdi-refresh-auto
                            </v-icon>
                        </div>
                    </td>
                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <div v-html="item.project_label"></div>
                    </td>
                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">{{ item.date_initialized_label }}</td>
                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )" v-html="item.close_date_label"></td>

                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <v-icon :class="item.expedite ? 'success--text text--darken-2' : ''">
                            {{ item.expedite ? 'mdi-checkbox-marked' : '' }}
                        </v-icon>
                    </td>

                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <!-- {{ item.status_label }} -->
                        <v-chip 
                            small
                            label
                            :color="item.status_progress_color"
                            text-color="white"
                            class="mr-1"
                            style="padding: .25rem .4rem !important">
                            <span class="font-weight-bold">
                                {{item.status_label }}
                            </span>
                            <v-icon v-if="item.is_archived" small class="ml-1">mdi-archive-alert</v-icon>
                        </v-chip>
                
                        <!-- <v-chip 
                            v-if="item.is_archived"
                            small
                            label
                            outlined
                            text-color="error darken-1"
                            style="border-color: transparent !important; margin-top: .25rem; padding: .4rem .4rem .4rem 0 !important;">
                            <v-icon small v-if="item.is_archived">mdi-archive-alert</v-icon>
                        </v-chip> -->
                    </td>
                    
                    <td v-if="item.ready_to_submit" class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        {{ item.current_stage }}
                        <span class="d-block">
                            <v-chip 
                                x-small
                                label
                                class="font-weight-bold"
                                text-color="white" 
                                color="error">
                                ready to submit
                            </v-chip>
                        </span>
                    </td>
                    <td v-else class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        {{ item.current_stage }}
                    </td>

                    <td class="text-start align-text-top">
                        <div v-if="item.multi_project && item.multi_project_modules.length <= 2" class="ma-0 pa-0">
                            <v-tooltip
                                v-for="mod in item.multi_project_modules"
                                :key="mod.project_id"
                                bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-chip
                                        x-small
                                        label
                                        outlined
                                        clickable
                                        class="mr-1 multi-project-chip"
                                        :color="mod.status_color"
                                        @click.stop="openProjectDetailsFromTag(mod)"
                                        v-bind="attrs"
                                        v-on="on">
                                        {{ mod.module_name }} : {{ mod.status_text }}
                                    </v-chip>
                                </template>
                                <span>{{ getProjectTitleTooltip(mod) }}</span>
                            </v-tooltip>
                        </div>
                        <div v-else-if="item.multi_project && item.multi_project_modules.length > 2" class="ma-0 pa-0">
                            <v-menu
                                open-on-hover
                                top
                                offset-y
                                offset-x
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                            <v-chip
                                                x-small
                                                label
                                                outlined
                                                clickable
                                                class="mr-1 multi-project-chip"
                                                color="primary darken-1"
                                                @mouseover.stop="openMultiProjectModules( item.multi_project_modules )"
                                                v-bind="{ ...attrs, ...tooltipAttrs }"
                                                v-on="{ ...on, ...tooltipOn }">
                                                {{ item.multi_project_modules.length }} Projects
                                            </v-chip>
                                        </template>
                                        <span>{{ getMultiProjectTitlesTooltip(item.multi_project_modules) }}</span>
                                    </v-tooltip>
                                </template>

                                <v-list>
                                    <v-list-item
                                        v-for="(mod, index) in multi_project_modules"
                                        :key="index"
                                        @click.stop="openProjectDetailsFromTag(mod)"
                                    >
                                        <v-list-item-title>
                                            <div class="font-weight-light py-1" :class="mod.status_text_color">
                                                {{ mod.module_name }}: {{ mod.status_text }}
                                            </div>
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </td>

                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <div v-if="item.next_call_label" v-html="item.next_call_label"></div>
                    </td>
                    
                    <!-- <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <div v-if="item.remaining_call_label" v-html="item.remaining_call_label"></div>
                    </td> -->

                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <div v-html="item.calls_data_label"></div>
                    </td>
                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <div class="text-left">
                            <div v-if="item.last_contacted_label" v-html="item.last_contacted_label"
                                class="d-block mb-4">
                            </div>
                            <div @click.stop="openProjectActivity( item )" 
                                class="d-block primary--text text--darken-1 font-weight-bold text-decoration-none">
                                View Activity
                                <v-icon 
                                    small
                                    color="primary">
                                    mdi-arrow-right
                                </v-icon>
                            </div>
                        </div>
                    </td>
                    
                    <!-- <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">
                        <v-progress-circular 
                            :value="item.complete == 0 ? 2 : Math.ceil(item.complete)"
                            :class="item.status_color"
                            :rotate="-90"
                            class="pa-0 ma-0" 
                            size="43" 
                            width="4" >
                            
                            <div :style="item.complete == 100 ? 'font-size: .8rem' : 'font-size: .9rem'"
                                :class="item.status_text_color + ' font-weight-bold'">
                                {{ item.complete }}
                            </div>
                        </v-progress-circular>
                    </td>
                     -->
                    <!-- <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">{{ item.date_saved_label }}</td> -->

                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">{{ item.document_status_label }}</td>
                    <td class="text-start align-text-top" @click.stop="openProjectDetails( item )">{{ item.entity_filing_status_label }}</td>
                    
                    <td class="text-end align-text-top caption"
                        @click="handleCopyProjectId( item.project_id )">

                        <v-tooltip bottom content-class="contact_tooltip">
                            <template v-slot:activator="{ on, attrs }">
                                <span 
                                    :id="`copy-pid-${item.project_id}`"
                                    class="font-weight-thin primary--text text--darken-1"
                                    v-bind="attrs"
                                    v-on="on">
                                    <v-icon 
                                        x-small 
                                        class="mr-1"
                                        color="primary">
                                        mdi-content-copy
                                    </v-icon>
                                    {{ item.project_id }}
                                </span>
                            </template>
                            <span>{{ trigger_copied ? 'copied' : 'click to copy' }}</span>
                        </v-tooltip>
                    </td>
                    <td class="text-start align-text-top"></td>
                </tr>
            </template>
            
            <!-- Expanded Row Template -->
            <template v-slot:expanded-item="{ item }">
                <tr>
                    <td colspan="16">
                        <WysiwygEditor
                            :key="`quick-notes-${item.dashboard_row_key}`"
                            :value="item.quick_notes || ''"
                            @input="val => $set(item, 'quick_notes', val)"
                            style="width: 30%;"
                            :toolbar="quickNotesToolbar">
                        </WysiwygEditor>
                        <div class="d-block d-flex justify-end"
                            style="width: 30%;">
                            <v-btn
                                v-show="quick_note_saved_res"
                                class="mr-2 mt-1 d-inline white--text font-weight-bold"
                                :class="quick_note_saved_res ? '' : 'fade-out-btn'"
                                color="accent"
                                elevation="0"
                                x-small
                                text>
                                Saved
                            </v-btn>
                            <v-btn 
                                color="primary" 
                                class="d-flex d-inline font-weight-bold ml-1 mt-1 mb-1"
                                outlined
                                x-small
                                @click="saveQuickNotes({project_id: item.project_id, quick_notes: item.quick_notes})">
                                save note
                            </v-btn>
                        </div>
                    </td>
                </tr>
            </template>
        </v-data-table>
    </div>
</template>


<script>
    import axios from "axios";
    import Cookie from 'js-cookie';
    import appUrls from '../helpers/environment'
    import { safeParseJson } from '../helpers/safe-json'
    import { searchTableRows } from '../helpers/tableSearch'

    import { mdiAccount, mdiPencil, mdiDelete, mdiMagnify } from '@mdi/js'

    import { capitalizeFirstWord, setStatusData, dataTimeDisplayFormatter } from './utilities'
    import { moduleServices } from '../helpers/constants'

    import ProjectDetails from './ProjectDetails'
    import ProjectActivity from './ActivityLogs'
    import UserData_Dashboard from './UserData_Dashboard'
    import WysiwygEditor from "./WysiwygEditor.vue";
    import { quickNotesToolbar } from './shared/editorToolbars'

    const PAGE_SIZE_OPTIONS = [25, 50, 100]
    const DEFAULT_PAGE_SIZE = 50

    export default {

        components: {
            ProjectDetails,
            ProjectActivity,
            UserData_Dashboard,
            WysiwygEditor
        },

        data: () => ({
            expanded: [],
            name: 'Projects',
            icons: {
                accountIcon: mdiAccount,
                pencilIcon: mdiPencil,
                deleteIcon: mdiDelete,
                magnifyIcon: mdiMagnify,
            },
            dialog: false,
            projectDataDialog: false,
            errorMsg: '',
            searchInput: '',
            table_loading: false,
            search: '',
            filterby: {
                is_annual: false,
                filter_archived: false,
                module: [],
                // service_levels: [],
                status: ['open', 'review', 'processing'],
                current_stage: [],
                due_date: [],
                search: '',
                close_date_range: [],
                initialized_date_range: [],
            },
            // PAGED SO THE TABLE ONLY RENDERS ONE PAGE OF ROWS AT A TIME
            options: {
                // sortBy: ['close_date'],
                // sortDesc: [false],
                // Initialize other options as needed
                itemsPerPage: DEFAULT_PAGE_SIZE,
                multiSort: true
            },
            PAGE_SIZE_OPTIONS,
            headers: [    
                { text: 'Client Name(s)',       value: 'client_info_stringified',    align: 'start',  sortable: true, width: '220px', class: 'tbl_headers' },
                { text: 'Module Name',          value: 'module_name',                align: 'start',  sortable: true, width: '190px' },
                { text: 'Project Title(s)',     value: 'project_label',              align: 'start',  sortable: true, width: '250px' },
                { text: 'Date Started',         value: 'date_initialized',           align: 'start',  sortable: true, width: '115px' },
                { text: 'Est. Completion',      value: 'close_date',                 align: 'start',  sortable: true, width: '130px' },  
                { text: 'Expedite',             value: 'expedite',                   align: 'start',  sortable: true, width: '96px'  },
                { text: 'Status',               value: 'status_label',               align: 'start',  sortable: true, width: '170px' },
                { text: 'Current Stage',        value: 'current_stage',              align: 'start',  sortable: true, width: '170px' },
                { text: 'Multi-Service',        value: 'multi_project',              align: 'start',  sortable: true, width: '180px' },                
                { text: 'Next Call',            value: 'next_call_label',            align: 'start',  sortable: true, width: '110px' },
                { text: 'Calls Used',           value: 'calls_data_label',           align: 'start',  sortable: true, width: '160px' },
                { text: 'Last Contacted',       value: 'last_contacted',             align: 'start',  sortable: true, width: '150px' },
                { text: 'Document Status',      value: 'document_status_label',      align: 'start',  sortable: true, width: '150px' },
                { text: 'Entity Filing Status', value: 'entity_filing_status_label', align: 'start',  sortable: true, width: '150px' },
                { text: 'Project ID',           value: 'project_id',                 align: 'end',    sortable: true, width: '296px' },
                { text: '',                     value: 'data-table-expand'},
                // { text: 'Additional Calls',     value: 'remaining_call_label',    align: 'start',  sortable: true, width: '130px' },
                // { text: 'Questionnaire %',    value: 'questionnaire_percentage',align: 'start',   sortable: true, width: '150px' },
                // { text: 'Project %',            value: 'complete',                align: 'start',  sortable: true, width: '100px' },  
                // { text: 'Last Saved Date',      value: 'date_saved',              align: 'start',  sortable: true, width: '130px' },             
            ],
            services: moduleServices,
            // service_levels: serviceLevels,
            due_date_filter: [
                { id: 1, text: 'Past Due',       value: 'past_due' },
                { id: 2, text: 'Due Today',      value: 'due_today' },
                { id: 3, text: 'Due Tomorrow',   value: 'due_tomorrow' },
                { id: 4, text: 'Due This Week',  value: 'due_this_week' },
                { id: 5, text: 'Due This Month', value: 'due_this_month' },
            ],
            project_statuses: [],
            current_stage_options: [],
            users_sortorder: ['owner', 'secondary', 'partner'],
            // multiproject_sortorder: ['processing', 'review', 'open', 'waiting', 'hold', 'complete', 'closed'],
            multi_project_modules: {},
            panel: [0],
            modules: [],
            projects: [],
            project_status_metadata: [],
            filteredProjects: [],
            filteredProjectsPerStatus: [],
            filtered_projects_cnt: 0,
            admin_matches: [],
            menu1: false,
            this_userId: '',
            this_projectId: '',
            this_clientInfo: [],
            debounceTimeout: null,
            quick_note_saved_res: false,
            projectActivityDialog: false,
            projectForActivity: '',
            userForActivity: '',
            trigger_copied: false,
            statusOptions: [],
            closeDateRangeMenu: false,
            initDateRangeMenu: false,
            allProjectsRaw: [],
            quickNotesToolbar
        }),
        
        computed: {
            // EVERY PROJECT MATCHING THE FILTERS + SEARCH (THE TABLE ONLY RENDERS THE CURRENT PAGE OF THESE)
            searchedProjects () {
                return searchTableRows(this.filteredProjects, this.headers, this.search)
            },
            // True when the filters differ from the default dashboard view
            hasActiveFilters () {
                const normalize = filters => JSON.stringify(Object.keys(filters).sort().map( key => {
                    const value = filters[key]
                    return [key, Array.isArray(value) ? [...value].sort() : value]
                }))
                return normalize({ ...this.filterby, search: this.search || '' }) !== normalize(this.defaultFilters())
            },

            showFilterArchiveToggle () {
                // return ['root','super'].includes(Cookie.get('levs'))
                return true
            },
            allSelected () {
                return this.filterby.status.length === this.project_statuses.length;
            },
            closeDateRangeText () {
                if (!this.filterby.close_date_range || !this.filterby.close_date_range.length) return '';
                return this.filterby.close_date_range.join(' ~ ');
            },
            initDateRangeText () {
                if (!this.filterby.initialized_date_range || !this.filterby.initialized_date_range.length) return '';
                return this.filterby.initialized_date_range.join(' ~ ');
            },
        },
        
        watch: {
            // TOTALS & STATUS COUNTS COVER EVERY MATCHING PROJECT, NOT JUST THE PAGE ON SCREEN
            searchedProjects (rows) {
                this.filtered_projects_cnt = rows.length
                this.calculateFilteredProjects(rows)
                localStorage.setItem('projectDashboardFilters', JSON.stringify(this.filterby));
            },
            dialog (val) {
                val || this.close()
            },
            search (val) {
                this.filterby.search = val
                localStorage.setItem('projectDashboardFilters', JSON.stringify(this.filterby))
            },
            // projects () {
            //     this.filterProjects();
            // },
            // projectActivityDialog ( val ) {
            //     if ( !val ) {
            //         this.projectActivityReady = false
            //     }
            // }            
            // filterby: {
            //     handler (val) {
            //         console.log('filterby changed', val)
            //         this.filterProjects()
            //     }, deep: true
            // }
        },
        
        created () {
            this.getStatusData()
            this.getModules()
            
            const savedOptions = localStorage.getItem('projectDashboardOptions');
            if ( savedOptions ) {
                const parsedOptions = safeParseJson(savedOptions, null)
                if (parsedOptions && typeof parsedOptions === 'object') {
                    // OLDER SAVED OPTIONS SHOWED EVERY ROW (-1); START THOSE ON A NORMAL PAGE SIZE
                    this.options = {
                        ...parsedOptions,
                        page: 1,
                        itemsPerPage: PAGE_SIZE_OPTIONS.includes(parsedOptions.itemsPerPage) ? parsedOptions.itemsPerPage : DEFAULT_PAGE_SIZE
                    }
                }
            }

            const savedFilters = localStorage.getItem('projectDashboardFilters');
            if ( savedFilters ) {
                const parsedFilters = safeParseJson(savedFilters, null)
                if (parsedFilters && typeof parsedFilters === 'object') {
                    this.filterby = parsedFilters
                }
                this.filterby.due_date = this.filterby.due_date.length ? this.filterby.due_date : []
                this.filterby.status = this.filterby.status.length ? this.filterby.status : ['open', 'review', 'processing']
                // Saved stages are validated against the filtered projects in filterProjects()
                this.filterby.current_stage = Array.isArray(this.filterby.current_stage) ? this.filterby.current_stage : []
                this.search = this.filterby.search
                this.filterby.is_annual = false
                this.filterby.filter_archived = false
            } else {
                this.setModuleSelection()
            }
        },

        mounted () {
            this.getProjects()
        },

        methods: {
            debouncedSearch() {
                clearTimeout(this.debounceTimeout);
                this.debounceTimeout = setTimeout(() => {
                    this.getProjects()
                }, 500);
            },

            openProjectDetails: async function ( item ) {
                // console.log(item)
                this.projectDataDialog = true
                this.this_userId = item.client_info[0].user_id
                this.this_projectId = item.project_id
                this.this_clientInfo = item.client_info
            },

            openProjectDetailsFromTag: function ( projectRef ) {
                if ( !projectRef || !projectRef.user_id || !projectRef.project_id ) return

                this.projectDataDialog = true
                this.this_userId = projectRef.user_id
                this.this_projectId = projectRef.project_id
                this.this_clientInfo = []
            },

            getProjectTitleTooltip: function ( projectRef ) {
                const projectTitle = projectRef && projectRef.project_title ? String(projectRef.project_title).trim() : ''
                return projectTitle || 'n/a'
            },

            getMultiProjectTitlesTooltip: function ( projects ) {
                if ( !Array.isArray(projects) || !projects.length ) return 'n/a'

                return projects
                    .map(project => this.getProjectTitleTooltip(project))
                    .join(' | ')
            },

            closeDetailDialog: function ( val ) {
                this.projectDataDialog = false
                this.this_userId = ''
                this.this_projectId = ''
                this.this_clientInfo = []
                this.getProjects()
            },

            openProjectActivity: async function ( item ) {
                this.projectForActivity = item.project_id
                this.userForActivity = item.user_id
                this.projectActivityDialog = true
            },

            getStatusData: async function () {
                try {
                    const get_project_status_data = await axios.get(`${appUrls.api}/api/project_status_data`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                    })

                    this.project_statuses = get_project_status_data.data.status_data
                    this.project_statuses.forEach(obj => {
                        obj.count = 0;
                    });

                } catch ( error ) {
                    console.log(error);
                }
            },

            saveQuickNotes: async function ( project ) {
                try {
                    console.log(project)
                    const quick_note_rs = await axios.post(`${appUrls.api}/api/admin/save_quick_notes`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        project
                    })

                    if ( quick_note_rs.data ) {
                        // Keep every row for this project (one per user) in sync with the saved note
                        if ( quick_note_rs.data.outcome ) {
                            this.projects
                                .filter( prj => prj.project_id === project.project_id )
                                .forEach( prj => this.$set( prj, 'quick_notes', project.quick_notes ) )
                        }
                        this.quick_note_saved_res = quick_note_rs.data.outcome
                        setTimeout(() => {
                            this.quick_note_saved_res = false
                        }, 2000)
                    }
                } catch ( error ) {
                    console.log(error);
                }
            },

            handleCopyProjectId: function ( id ) {
                navigator.clipboard.writeText(document.querySelector(`span#copy-pid-${id}`).innerText.trim())
                this.trigger_copied = true;
                setTimeout(() => { this.trigger_copied = false; }, 3000);
            },

            openMultiProjectModules: function ( modules ) {
                this.multi_project_modules = modules
            },

            getArchivedProjects: function () {
                this.getProjects()
            },

            refreshProjects: function () {
                this.getProjects(true)
            },

            getProjects: async function ( forceRefresh = false ) {
                try {
                    this.table_loading = true
                    const { data } = await axios.get(`${appUrls.api}/api/admin/archived/${this.filterby.filter_archived}/projects`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        params: forceRefresh ? { forceRefresh: true } : {}
                    });

                    if (!data.outcome) return;

                    // 1. FAST FILTERING (Do this before expensive transformations)
                    // Store IDs in a Set for O(1) lookup speed
                    const moduleIds = new Set(this.modules.map(m => m.module_id));
                    const filteredRaw = data.projects.filter(prj => moduleIds.has(prj.module_id));

                    // 2. MINIMAL STORAGE
                    // Store the raw results. We will process labels only when needed.
                    this.allProjectsRaw = filteredRaw;
                    
                    // 3. CHUNKED PROCESSING (Non-blocking)
                    this.processProjectsInChunks(filteredRaw);

                } catch (err) {
                    console.error("Failed to fetch projects", err);
                }
            },

            processProjectsInChunks: function (projects) {
                const CHUNK_SIZE = 50; 
                let index = 0;
                this.projects = []; // Clear current list

                const processNext = () => {
                    const chunk = projects.slice(index, index + CHUNK_SIZE);
                    
                    const processedChunk = chunk.map((prj, chunkIndex) => {
                        // Move all that heavy label logic into a standalone helper function
                        return this.transformProjectData(prj, index + chunkIndex);
                    });

                    this.projects.push(...processedChunk);
                    index += CHUNK_SIZE;

                    if (index < projects.length) {
                        // schedule next chunk in next animation frame to keep UI responsive
                        requestAnimationFrame(processNext);
                    } else {
                        this.filterProjects(); // Final sync
                    }
                };

                processNext();
            },

            transformProjectData: function(prj, sourceIndex = 0) {
                // Optimization: Cache standard values
                const mod = this.modules.find(obj => obj.module_id === prj.module_id);
                if (!mod) return prj;

                // Use a simpler date handling approach (avoiding redundant formatDate calls)
                const getTs = (dateStr) => dateStr ? new Date(dateStr).getTime() : 0;

                // Build labels...
                // [Insert your existing prj.label logic here, but optimized]
                
                return {
                    ...prj,
                    dashboard_row_key: `${prj.project_id}-${prj.user_id}-${sourceIndex}`,
                    next_call_label: this.calcCallLabel( prj ),
                    close_date_label: this.calcCloseLabel( prj ).close_date_label,
                    close_date: this.calcCloseLabel( prj ).close_date,
                    last_contacted_label: this.calcLastCallLabel( prj ).last_contacted_label,
                    last_contacted: this.calcLastCallLabel( prj ).last_contacted,
                    remaining_call_label: this.calcRemainingCallData( prj ),
                    calls_data: this.calcCallsUsed( prj ).calls_data,
                    calls_data_label: this.calcCallsUsed( prj ).calls_data_label,
                    date_initialized_label: this.calcProjectInitializationData( prj ).date_initialized_label,
                    date_initialized: this.calcProjectInitializationData( prj ).date_initialized,
                    date_saved_label: this.calcLastSavedDate( prj ).date_saved_label,
                    date_saved: this.calcLastSavedDate( prj ).date_saved,
                    ready_to_submit: this.calcQuestionnaireMetrics( prj ).ready_to_submit,
                    questionnaire_percentage: this.calcQuestionnaireMetrics( prj ).questionnaire_percentage,
                    internal_project_title: this.calcProjectTitleLabel( prj ).internal_project_title, 
                    project_title: this.calcProjectTitleLabel( prj ).project_title, 
                    project_label: this.calcProjectTitleLabel( prj ).project_label,
                    client_info_stringified: this.calcSortedUsers( prj ),
                    status_label: this.calcStatusMetaData( prj ).status_label,
                    status_color: this.calcStatusMetaData( prj ).status_color, 
                    status_progress_color: this.calcStatusMetaData( prj ).status_progress_color, 
                    status_text_color: this.calcStatusMetaData( prj ).status_text_color
                };
            },

            calcCallLabel: function( prj ) {
                const nextCallDate   = prj.scheduled_calls != false ? prj.scheduled_calls[0].next_call.next_call_date : false;
                const next_call_date = nextCallDate != false ? new Date(nextCallDate).getTime() : 0;
                const next_call_info = next_call_date != 0 ? this.formatDate(nextCallDate) : ``;

                return next_call_info; 
            },

            calcCloseLabel: function( prj ) {
                const this_close_date   = prj.close_date != null ? new Date(prj.close_date).getTime() : 0;
                const this_status_check = prj.status.startsWith('hold') ? 'hold' : prj.status
                const close_date_label  = prj.close_date != null ? 
                    ( this.isDate5DaysAwayOrLater(prj.close_date) && ['open', 'review', 'processing', 'hold'].includes(this_status_check) ) ? 
                    `<span class="error--text">${this.formatDate(prj.close_date)}</span>` : this.formatDate(prj.close_date) : 'NA';
                const close_date        = this_close_date != 0 ? new Date(prj.close_date).getTime() : 0;

                return {
                    close_date_label, 
                    close_date
                };
            },

            calcLastCallLabel: function( prj ) {
                const this_last_contacted_date = prj.last_contacted_data.last_contacted_date != false ? new Date(prj.last_contacted_data.last_contacted_date).getTime() : 0;
                const last_contacted_label     = this_last_contacted_date != 0 ? `<span class="error--text">${capitalizeFirstWord(prj.last_contacted_data.last_contacted_log_type, 'first')}: ${this.formatDate(prj.last_contacted_data.last_contacted_date)}</span>` : ``
                const last_contacted           = this_last_contacted_date
                
                return {
                    last_contacted_label, 
                    last_contacted
                };
            },

            calcCallsUsed: function( prj ) {
                const calls_data       = prj.module_calls ? `${prj.calls_used}/${prj.module_calls}` : 0;
                const calls_data_label = calls_data != 0 ? calls_data+` calls used` : 'NA'

                return {
                    calls_data,
                    calls_data_label
                };
            },

            calcRemainingCallData: function( prj ) {
                const remainingCallData    = prj.scheduled_calls != false ? prj.scheduled_calls[0].remaining : false;
                const remaining_call_info  = remainingCallData ? remainingCallData.map( call => { return this.formatDate(call.date) }) : []
                const remaining_call_label = Array.isArray(remaining_call_info) && remaining_call_info.length > 0 ? remaining_call_info.join('<br>') : ``
                
                return remaining_call_label;
            },

            calcProjectInitializationData: function( prj ) {
                const this_date_initialized  = prj.date_initialized != null ? new Date(prj.date_initialized).getTime() : 0;
                const date_initialized_label = this_date_initialized != 0 ? this.formatDate(prj.date_initialized) : 'NA';
                const date_initialized       = this_date_initialized != 0 ? new Date(prj.date_initialized).getTime() : 0;
                
                return {
                    date_initialized_label, 
                    date_initialized
                };
            },

            calcLastSavedDate:  function( prj ) {
                const this_date_saved  = prj.date_saved != null ? new Date(prj.date_saved).getTime() : 0;
                const date_saved_label = this_date_saved != 0 ? this.formatDate(prj.date_saved) : 'NA';
                const date_saved       = this_date_saved != 0 ? new Date(prj.date_saved).getTime() : 0;
                
                return {
                    date_saved_label, 
                    date_saved
                };
            },

            calcQuestionnaireMetrics: function( prj ) {
                const ready_to_submit          = ['open', 'closed'].includes(prj.status) && ( prj.questionnaire_progress != null && prj.questionnaire_progress.ready ) ? true : false
                const questionnaire_percentage = prj.questionnaire_progress != null ? Math.ceil((prj.questionnaire_progress.finished / prj.questionnaire_progress.total ) * 100) : 0

                return {
                    ready_to_submit, 
                    questionnaire_percentage
                };
            },

            calcProjectTitleLabel: function( prj ) {
                const internal_project_title = prj.internal_project_title != null ? prj.internal_project_title : ''
                const project_title          = prj.project_title != null ? prj.project_title : ''
                const internal_title         = prj.internal_project_title != null ? prj.internal_project_title : ''
                const user_prj_title         = prj.project_title != null ? prj.project_title : ''
                // const mod_divider         = internal_title != '' || user_prj_title != '' ? ' :: ' : ': '
                const title_divider          = internal_title != '' && user_prj_title != '' ? ' :: ' : ''
                const project_label          = `
                    <span class="font-weight-thin grey--text">${internal_title}</span>
                    <span class="font-weight-thin grey--text text--darken-2">${title_divider}${user_prj_title}</span>`

                return {
                    internal_project_title, 
                    project_title, 
                    project_label
                };
            },

            calcSortedUsers: function ( prj ) {
                const sortedUsers = prj.client_info.sort((a, b) => {
                    const aIndex = this.users_sortorder.indexOf(a.user_type);
                    const bIndex = this.users_sortorder.indexOf(b.user_type);
                    return aIndex - bIndex;
                });
                
                return JSON.stringify( sortedUsers )
            },

            calcStatusMetaData: function ( prj ) {
                const status_color_data     = setStatusData(prj.status, this.project_statuses)
                const status_label          = status_color_data.status_internal_label
                const status_color          = status_color_data.status_color
                const status_progress_color = status_color_data.status_progress_color
                const status_text_color     = status_color_data.status_text_color

                return {
                    status_label, 
                    status_color, 
                    status_progress_color, 
                    status_text_color
                };
            },

            getModules: async function () {
                const all_modules = await axios.get(`${appUrls.api}/api/admin//submodule/false/all_modules`, {
                    headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                })
                
                if ( all_modules.data.outcome ) {
                    this.modules = all_modules.data.modules;

                    let admin_modules = this.modules.map(mod => {
                        if ( mod.module_key != 'buyer' ) {
                            let found = mod.srg_team.experts.map( x => {
                                if ( x.email == Cookie.get('srge') ) {
                                    return mod.module_key
                                }
                            })
                            if ( found.includes(mod.module_key) ) {
                                return mod.module_key
                            }
                        }
                    })
                    admin_modules = admin_modules.filter( Boolean )

                    let admin_matches = admin_modules.map(key => {
                        const service_match = this.services.find(service => service.value.includes(key))
                        return service_match ? service_match.id : null
                    }).filter(Boolean)
                    this.admin_matches = admin_matches
                }
            },

            doesArrayExist: function (target, source) {
                const targetString = JSON.stringify(target);

                return source.some(innerArray => {
                    return JSON.stringify(innerArray) === targetString;
                });
            },

            formatDate: function ( date ) {
                if (!date) return '';
                
                const options = {
                    month: '2-digit', 
                    day: '2-digit', 
                    year: 'numeric'
                }
                return dataTimeDisplayFormatter(date, options)
            },

            isDate5DaysAwayOrLater: function ( date ) {
                const today = new Date();
                const targetDate = new Date(date);

                const targetDateMilliseconds = targetDate.getTime()
                const fiveDaysFromToday = today.getTime() + (1000 * 3600 * 24 * 5)
                //const difference = today.getTime() - targetDate.getTime();
                // const differenceInDays = Math.ceil(difference / (1000 * 3600 * 24));

                return targetDateMilliseconds <= fiveDaysFromToday;
            },

            defaultFilters: function () {
                const admin_modules = this.services.filter(service => this.admin_matches.includes(service.id))
                return {
                    is_annual: false,
                    filter_archived: false,
                    module: admin_modules.map(service => service.value),
                    status: ['open', 'review', 'processing'],
                    current_stage: [],
                    due_date: [],
                    search: '',
                    close_date_range: [],
                    initialized_date_range: [],
                }
            },

            // Reset back to the default dashboard view (same as a first visit)
            clearFilters: function () {
                const wasArchived = this.filterby.filter_archived
                this.search = ''
                this.filterby = this.defaultFilters()
                localStorage.setItem('projectDashboardFilters', JSON.stringify(this.filterby))

                // Archived projects come from a different endpoint, so re-pull when leaving that view
                wasArchived ? this.getProjects() : this.filterProjects()
            },

            setModuleSelection: function () {
                const admin_modules = this.services.filter(service => this.admin_matches.includes(service.id))
                this.filterby.module = admin_modules.map(service => service.value)
                // this.filterby.service_levels = ['starter', 'premium', 'elite', 'lite']
                this.filterby.due_date = []
                this.filterby.is_annual = false
                this.filterby.filter_archived = false
                this.filterby.search = this.search
            },

            onUpdateOptions(newOptions) {
                this.options = newOptions;
                // Save sort options to localStorage
                localStorage.setItem('projectDashboardOptions', JSON.stringify(newOptions));
            },

            isPastDue ( date ) {
                if (date !== 0) {
                
                    const givenDate = new Date(date).toISOString().split('T')[0]
                    const today = new Date().toISOString().split('T')[0]

                    const givenDateMilliseconds = new Date(givenDate).getTime()
                    const todayMilliseconds = new Date(today).getTime()

                    return givenDateMilliseconds < todayMilliseconds
                }
            },

            isToday ( date ) {
                const givenDate = new Date(date).toISOString().split('T')[0]
                const today = new Date().toISOString().split('T')[0]

                const givenDateMilliseconds = new Date(givenDate).getTime()
                const todayMilliseconds = new Date(today).getTime()

                return todayMilliseconds === givenDateMilliseconds
            },

            isTomorrow ( date ) {
                const givenDate = new Date(date).toISOString().split('T')[0]
                let tomorrow = new Date().toISOString().split('T')[0]
                tomorrow = new Date(tomorrow)
                tomorrow.setDate(tomorrow.getDate() + 1) // Move to tomorrow

                const givenDateMilliseconds = new Date(givenDate).getTime()
                const tomorrowMilliseconds = tomorrow.getTime()

                return tomorrowMilliseconds === givenDateMilliseconds
            },

            isInSameWeek ( date ) {
                let now = new Date()

                let startOfWeek = new Date(now)
                startOfWeek.setDate(now.getDate() - now.getDay()) // Start of the week (Sunday)
                startOfWeek.setHours(0, 0, 0, 0)

                let endOfWeek = new Date(startOfWeek)
                endOfWeek.setDate(startOfWeek.getDate() + 6) // End of the week (Saturday)

                // RESET DATES WITHOUT TIME
                now = new Date().toISOString().split('T')[0]
                startOfWeek = startOfWeek.toISOString().split('T')[0]
                endOfWeek = endOfWeek.toISOString().split('T')[0]
                const givenDate = new Date(date).toISOString().split('T')[0]

                // CONVERT TO MILLISECONDS
                const givenDateMilliseconds = new Date(givenDate).getTime()
                const startOfWeekMilliseconds = new Date(startOfWeek).getTime()
                const endOfWeekMilliseconds = new Date(endOfWeek).getTime()
             
                return givenDateMilliseconds >= startOfWeekMilliseconds && givenDateMilliseconds <= endOfWeekMilliseconds                
            },

            isSameMonth ( date ) {
                const givenDate = new Date(date).toISOString().split('T')[0]
                const now = new Date().toISOString().split('T')[0]

                const givenDateFullYear = givenDate.split('-')[0]
                const givenDateMonth = givenDate.split('-')[1]
                
                const nowFullYear = now.split('-')[0]
                const nowMonth = now.split('-')[1]

                return givenDateFullYear === nowFullYear && givenDateMonth === nowMonth
            },

            removeDuplicates ( arr ) {
                return [...new Map(arr.map(item => [JSON.stringify(item), item])).values()]
            },

            // Method to toggle selection (either select all or deselect all)
            toggleSelectAll() {
                if (this.allSelected) {
                    // If all are selected, deselect all
                    this.filterby.status = [];
                } else {
                    // If not all are selected, select all
                    this.filterby.status = this.project_statuses.map(item => item.value);
                }
                // Trigger your filter function after selection change
                this.filterProjects();
            },

            clearCloseDateRange() {
                this.filterby.close_date_range = [];
                this.filterProjects();
            },
            clearInitDateRange() {
                this.filterby.initialized_date_range = [];
                this.filterProjects();
            },

            // Unique, alphabetized list of every current_stage value across the given projects
            compileCurrentStageOptions: function ( projects ) {
                const stages = new Set();
                projects.forEach( prj => {
                    if (prj.current_stage) stages.add(prj.current_stage)
                });
                return [...stages].sort( (a, b) => a.localeCompare(b) )
            },

            // Drop any selected stage that doesn't exist in the current filtered set of projects
            pruneCurrentStageFilter: function () {
                const valid_stages = new Set(this.current_stage_options);
                this.filterby.current_stage = (this.filterby.current_stage || []).filter( stage => valid_stages.has(stage) )
            },

            filterProjects: async function () {
                let filteredProjects = this.projects

                // LEAVING THIS HERE FOR WHEN I NEED TO TEST FILTERING OUT TEST PROJECTS
                // filteredProjects = filteredProjects.filter( prj => !prj.is_test )

                // 1. Filter Archived
                // if (this.filterby.filter_archived) {
                //     filteredProjects = filteredProjects.filter( prj => !prj.is_archived )
                // }

                // 2. Annual Projects Switch
                // const isValuationModule = (this.filterby.module.length && this.doesArrayExist(['valuation-starter', 'valuation-premium', 'valuation-elite'], this.filterby.module)) || !this.filterby.module.length;
                // if (isValuationModule && this.filterby.is_annual) {
                if ( this.filterby.is_annual ) {
                    filteredProjects = filteredProjects.filter( prj => prj.is_annual )
                }

                // 3. Module Filter
                if (this.filterby.module.length) {
                    let module_dataset = [].concat.apply([], this.filterby.module);
                    filteredProjects = filteredProjects.filter( prj => module_dataset.includes(prj.module_key) )
                }

                // 4. Service Levels Filter
                // if (this.filterby.service_levels.length) {
                //     let service_levels_dataset = [].concat.apply([], this.filterby.service_levels);
                //     filteredProjects = filteredProjects.filter( prj => {
                //         if (
                //             prj.module_key === 'entity-maintenance-program' || 
                //             prj.module_key === 'seller-advocacy' || 
                //             prj.module_key === 'succession' || 
                //             prj.module_key === 'deal-support' || 
                //             prj.module_key === 'merger' || 
                //             prj.module_key === 'entity' || prj.module_key === 'entity²' || 
                //             prj.module_key === 'core-value' || prj.module_key === 'practice-value' || prj.module_key === 'enterprise-value' || 
                //             prj.module_key === 'equity' ||
                //             prj.module_key === 'hr-solutions-employment-agreements' || prj.module_key === 'hr-solutions-comp-design' || prj.module_key === 'hr-solutions-comp-tsr' || 
                //             prj.module_key === 'contingency'
                //         ) return true;
                //         return service_levels_dataset.some( text => prj.module_key.indexOf(text) !== -1 )
                //     });
                // }

                // 5. Status Filter
                if (this.filterby.status.length) {
                    let status_dataset = [].concat.apply([], this.filterby.status);
                    filteredProjects = filteredProjects.filter( prj => status_dataset.includes(prj.status) )
                }

                // 6. Existing Due Date Quick Filters
                if (this.filterby.due_date.length) {
                    let due_date_dataset = [].concat.apply([], this.filterby.due_date);
                    filteredProjects = filteredProjects.filter( prj => {
                        const targetDate = prj.close_date;
                        return due_date_dataset.some(val => {
                            if (val === 'past_due') return this.isPastDue(targetDate);
                            if (val === 'due_today') return this.isToday(targetDate);
                            if (val === 'due_tomorrow') return this.isTomorrow(targetDate);
                            if (val === 'due_this_week') return this.isInSameWeek(targetDate);
                            if (val === 'due_this_month') return this.isSameMonth(targetDate);
                            return false;
                        });
                    });
                }

                // 7. NEW: Close Date Range Filter
                if (this.filterby.close_date_range && this.filterby.close_date_range.length === 2) {
                    // Sort range to ensure [start, end] even if user clicked end date first
                    const range = [...this.filterby.close_date_range].sort();
                    const start = new Date(range[0]);
                    const end = new Date(range[1]);
                    end.setHours(23, 59, 59, 999); // Include the full end day

                    filteredProjects = filteredProjects.filter(prj => {
                        if (!prj.close_date) return false;
                        const prjDate = new Date(prj.close_date);
                        return prjDate >= start && prjDate <= end;
                    });
                }

                // 8. NEW: Initialized Date Range Filter
                if (this.filterby.initialized_date_range && this.filterby.initialized_date_range.length === 2) {
                    // Sort range to ensure [start, end] even if user clicked end date first
                    const range = [...this.filterby.initialized_date_range].sort();
                    const start = new Date(range[0]);
                    const end = new Date(range[1]);
                    end.setHours(23, 59, 59, 999); // Include the full end day

                    filteredProjects = filteredProjects.filter( prj => {
                        if (!prj.date_initialized) return false;
                        const prjDate = new Date(prj.date_initialized);
                        return prjDate >= start && prjDate <= end;
                    });
                }

                // 9. Current Stage Filter (runs last so its options reflect every other filter)
                this.current_stage_options = this.compileCurrentStageOptions(filteredProjects)
                this.pruneCurrentStageFilter()
                if (this.filterby.current_stage.length) {
                    const stage_dataset = new Set(this.filterby.current_stage);
                    filteredProjects = filteredProjects.filter( prj => stage_dataset.has(prj.current_stage) )
                }

                this.calculateFilteredProjects(filteredProjects)

                this.filteredProjects = filteredProjects
                this.filtered_projects_cnt = this.filteredProjects.length
                localStorage.setItem('projectDashboardFilters', JSON.stringify(this.filterby));
                this.table_loading = false;
            },

            calculateFilteredProjects: async function ( filteredProjects ) {
                this.project_statuses.forEach(status => {
                    status.count = filteredProjects.filter(p => p.status === status.value).length;
                });

                this.project_status_metadata=[]
                this.project_statuses.forEach( status => {
                    if ( this.filterby.status.includes(status.value) ) {
                        this.project_status_metadata.push(`${this.project_statuses.filter( ps => ps.value == status.value )[0].internal_label}: <strong>${status.count}</strong>`)
                    }
                })
            }
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    .filter-container {
        position: sticky;
        top: 0;
        background-color: white;
        z-index: 1000;
        padding-top: 10px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    .view-height-max {
        height: 100vh;
    }
    .v-dialog__content {
        width: 98vw !important;
    }
    #projects_wrapper {
        width: 100%;
        margin: 0 auto;
        padding: 3rem 0 0 0;
        display: flex;
        flex-direction: column;
        /* height: 100vh; */
    }
    .header_bg_color {
        background-color: var(--v-primary-darken3)
    }
    tr:nth-of-type(even) {
        background-color: rgba(223, 233, 236, .25);
    }
    td.no_extra_border {
        border-bottom: 0 !important;
    }
    .v-data-table__wrapper {
        overflow-x: auto !important;
    }
    .title {
        padding: .5rem;
    }
    .multi_project_modules {
        font-size: .70rem;
        font-weight: lighter;
    }
    #new_btn {
        width: 100%;
        text-align: right;
    }
    .listing_row {
        text-align: left;
    }
    .tbl_headers {
        padding: 0 !important;
    }
    .dialog_title_bkg {
        background-color: #2196f3; /* #17a2b8 */
        color: #fff
    }
    .icon-size-small {
        width: 2.25rem;
    }
    .v-expansion-panel-header.v-expansion-panel-header {
        padding: 1em !important;
        margin: 0 !important;
    }
    .v-expansion-panel-header.v-expansion-panel-header--active,
    .v-expansion-panel-header.v-expansion-panel-header:hover {
        background: #eef7f8;
        padding: 1em !important;
        margin: 0 !important;
        /* background-color: rgba(189, 235, 249, 0.25); */
    }
    >>> .v-expansion-panel-content__wrap {
        padding: .5em 0 !important; 
        /* margin: 0 !important;     */
    }
    .v-chip.v-chip--outlined.v-chip.v-chip.multi-project-chip {
        background-color: white !important;
        padding: .625rem .5rem !important; 
        margin-bottom: .35rem !important;
        font-weight: bold
    }
    .v-data-table { 
        overflow-x: scroll !important;
        overflow-y: scroll !important;
    }
    >>> .v-data-table .v-data-table__wrapper > table > thead > tr > th {
        padding: 0 1em 0 1em !important;
        font-size: x-small !important;
        color: #333 !important;
        margin: 0 !important;
        height: 36px !important;
        border-top: 1px solid #EDEDED !important;
    }
    >>> .v-data-table .v-data-table__wrapper > table > tbody > tr > td {
        padding: .8em 1em .8em 1em !important;
        margin: 0 !important;
        font-size: small !important;
        cursor: pointer !important;
    }
    >>> .v-data-table__expanded {
        background-color: #fff !important;
        padding: .8em 1em .8em 1em !important;
        height: 90px !important;
    }
    .text-underline {
        text-decoration: underline;
    }
    .fade-out-btn {
        transition: opacity 0.5s ease-out; /* Smooth fade-out transition */
    }
    .fade-out-btn[v-cloak] {
        display: none; /* Prevent flash before Vue mounts */
    }
    .fade-out-btn:not([v-show]) {
        opacity: 0; /* Fade out when hidden */
    }
    .align-text-top {
       vertical-align: top;
    }
    .drawer-content {
        height: 100%; /* Full height of the drawer */
        overflow-y: auto; /* Allow vertical scrolling */
    }
    /* Hide scrollbar but allow scrolling */
    .drawer-content::-webkit-scrollbar {
        display: none; /* For Chrome, Safari, and Edge */
    }
    .drawer-content {
        -ms-overflow-style: none; /* For Internet Explorer and Edge */
        scrollbar-width: none; /* For Firefox */
    }
    .spin-refresh {
        animation: spin-refresh 1s linear infinite;
    }
    @keyframes spin-refresh {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/Projects.vue